import { useEffect, useRef, useState } from "react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  ActionToastRegion,
  Button,
  clearActionToasts,
  SelectableTreePrefixes,
  showActionToast,
  Hierarchy as Tree,
  treeIdPrefix,
  TreeNode,
} from "@fidelix/fx-miranda";
import { useRequiredParams } from "~utils/url";
import { queryClient } from "~utils/query-client";
import { useUserContext } from "../user/user-context";
import { hasPermission } from "../utils/permission-helper";
import { Permissions } from "../const";
import { TreeProvider } from "./tree-context.provider";
import { fetchGraphicChildNodes } from "./tree.queries";
import { ApplyProcessTypeDialog } from "../graphics/apply-process-type-dialog";

const canPress: SelectableTreePrefixes = [
  treeIdPrefix.building,
  treeIdPrefix.device,
  treeIdPrefix.level,
  treeIdPrefix.deviceGraphicItem,
  treeIdPrefix.scadaGraphicItem,
  treeIdPrefix.scadaGraphic,
];

export function GraphicTree() {
  const { companyId } = useRequiredParams({ companyId: "number" });
  const { user } = useUserContext();
  const [selectedNodes, setSelectedNodes] = useState<TreeNode[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isUpdatingToastRef = useRef(false);

  const hasEditPermission = hasPermission(
    Permissions.SCADAGRAPHICS_EDIT,
    user?.permissions ?? [],
  );

  const fetchGraphicChildren = async (node: TreeNode): Promise<TreeNode[]> => {
    return queryClient.fetchQuery({
      queryKey: ["tree", node.treeId, companyId, "children"],
      queryFn: () => fetchGraphicChildNodes({ companyId, node }),
      // Add a little bit of staleTime to prevent refetching if the user is spamming chevrons
      staleTime: 15 * 1000,
    });
  };

  useEffect(() => {
    if (!hasEditPermission || selectedNodes.length === 0) {
      clearActionToasts();
      return;
    }

    const count = selectedNodes.length;
    isUpdatingToastRef.current = true;
    showActionToast(
      {
        title: t`${count} images selected`,
        children: (
          <Button
            variant="textUnderline"
            size="small"
            onPress={() => setIsDialogOpen(true)}
          >
            <Trans>Categorize</Trans>
          </Button>
        ),
      },
      {
        timeout: 0,
        onClose: () => {
          if (!isUpdatingToastRef.current) {
            setSelectedNodes([]);
          }
        },
      },
    );
    isUpdatingToastRef.current = false;
  }, [hasEditPermission, selectedNodes]);

  useEffect(() => {
    return () => {
      clearActionToasts();
    };
  }, []);

  const handleDialogSuccess = () => {
    setSelectedNodes([]);
    clearActionToasts();
    setIsDialogOpen(false);
  };

  return (
    <TreeProvider>
      <Tree
        texts={{
          searchPlaceholder: t`Search for a building`,
          noResultFound: t`No results found`,
          errorText: t`Something went wrong`,
          select: t`Select`,
          unselect: t`Unselect`,
        }}
        canPress={canPress}
        canLongPress={[]}
        selectedNodes={selectedNodes}
        onSelectionChange={hasEditPermission ? setSelectedNodes : undefined}
        asyncFetchChildrenConfig={{
          device: fetchGraphicChildren,
          scadaGraphic: fetchGraphicChildren,
        }}
      />
      <ActionToastRegion bottomPx={24} />
      {isDialogOpen && (
        <ApplyProcessTypeDialog
          treeIds={selectedNodes.map((n) => n.treeId)}
          onClose={() => setIsDialogOpen(false)}
          onSuccess={handleDialogSuccess}
        />
      )}
    </TreeProvider>
  );
}
